import React from 'react';
import { AlertTriangle, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface State { error: Error | null; key: number }

/** Catches render/hook failures on a route so users see a message + Retry, not a blank page. */
export default class RouteErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null, key: 0 };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[RouteErrorBoundary]', error, info.componentStack);
  }

  private retry = () => this.setState((s) => ({ error: null, key: s.key + 1 }));

  render() {
    if (!this.state.error) {
      return <React.Fragment key={this.state.key}>{this.props.children}</React.Fragment>;
    }
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div role="alert" className="max-w-md w-full rounded-lg border border-border bg-card/80 backdrop-blur-md p-6 text-card-foreground">
          <div className="flex items-center gap-2 font-semibold">
            <AlertTriangle className="h-5 w-5 text-destructive" aria-hidden="true" />
            Something went wrong loading this page
          </div>
          <p className="mt-2 text-sm text-muted-foreground break-words">{this.state.error.message}</p>
          <Button className="mt-4" onClick={this.retry}>
            <RotateCw className="h-4 w-4 mr-2" aria-hidden="true" /> Retry
          </Button>
        </div>
      </div>
    );
  }
}
